<template>
  <div v-if="shouldShowPopup" class="crime-popup" :class="{ 'mobile': isMobile }">
    <!-- Header Section -->
    <div class="popup-header">
      <h3 class="unit-name">{{ displayName }}</h3>
    </div>
    
    <!-- Content Section -->
    <div class="popup-content">
      <!-- Geographic Information -->
      <div class="geographic-section">
        <h4>Geographic Information</h4>
        <div class="geographic-info">
          <div v-if="stateCode" class="geographic-item">
            <span class="label">State:</span>
            <span class="value">{{ getStateName(stateCode) }} ({{ stateCode }})</span>
          </div>
          <div v-if="countyCode" class="geographic-item">
            <span class="label">County:</span>
            <span class="value">{{ getCountyName(countyCode) || `County ${countyCode}` }}</span>
          </div>
          <div v-if="tractCode" class="geographic-item">
            <span class="label">Census Tract:</span>
            <span class="value code-value">{{ formatTractCode(tractCode) }}</span>
          </div>
          <div v-if="blockGroupCode && unitType === 'block'" class="geographic-item">
            <span class="label">Block Group:</span>
            <span class="value code-value">{{ formatBlockGroupCode(blockGroupCode) }}</span>
          </div>
        </div>
      </div>

      <!-- Population Demographics -->
      <div v-if="actualPopulation || populationData || hasPopulationEstimate" class="population-section">
        <h4>Demographics</h4>
        <div class="population-info">
          <div v-if="actualPopulation || populationData" class="population-item">
            <span class="label">Population ({{ dataYear }}):</span>
            <span class="value population-value">{{ formatNumber(actualPopulation || populationData) }}</span>
          </div>
          <div v-else-if="hasPopulationEstimate" class="population-item">
            <span class="label">Est. Population:</span>
            <span class="value population-estimate">
              ~{{ formatNumber(estimatedPopulation) }}
              <small class="estimate-note">(typical range)</small>
            </span>
          </div>
          <div v-if="populationDensity" class="population-item">
            <span class="label">Density:</span>
            <span class="value">{{ populationDensity }} per sq mi</span>
          </div>
          <div v-if="householdCount" class="population-item">
            <span class="label">Households:</span>
            <span class="value">{{ formatNumber(householdCount) }}</span>
          </div>
        </div>
      </div>

      <!-- Risk Comparison Section -->
      <div v-if="riskLevel && riskLevel !== 'no_data' && comparisonMode !== 'viewport'" class="risk-comparison-section">
        <div class="section-header-with-info">
          <h4>Risk Assessment</h4>
          <div class="info-tooltip-container">
            <svg class="info-icon-small" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div class="info-tooltip">
              <strong>{{ getComparisonModeTitle() }}</strong>
              <p>{{ getComparisonModeExplanation() }}</p>
            </div>
          </div>
        </div>
        
        <div class="risk-info-grid">
          <div class="risk-info-item">
            <span class="risk-info-label">Risk Level:</span>
            <span 
              class="risk-badge"
              :class="getRiskBadgeClass(riskLevel)"
            >
              {{ formatRiskLevel(riskLevel) }}
            </span>
          </div>
          
          <div v-if="normalizedScore !== null && normalizedScore !== undefined" class="risk-info-item">
            <div class="label-with-tooltip">
              <span class="risk-info-label">Normalized Score:</span>
              <div class="info-tooltip-container">
                <svg class="info-icon-tiny" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div class="info-tooltip">
                  <strong>Normalized Score</strong>
                  <p>Position of this area's crime index within the comparison range, scaled to 0-100%. Higher scores indicate higher crime relative to the comparison group.</p>
                </div>
              </div>
            </div>
            <span class="risk-info-value">{{ normalizedScore.toFixed(1) }}%</span>
          </div>
          
          <div v-if="crimeIndex !== null && crimeIndex !== undefined" class="risk-info-item">
            <div class="label-with-tooltip">
              <span class="risk-info-label">Crime Index:</span>
              <div class="info-tooltip-container">
                <svg class="info-icon-tiny" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div class="info-tooltip">
                  <strong>Crime Index Value</strong>
                  <p>Standardized crime index where 100 = national average. This index is already normalized for population and directly comparable across areas.</p>
                  <p class="tooltip-example"><em>Example: Index of 150 means 50% higher crime than national average.</em></p>
                </div>
              </div>
            </div>
            <span class="risk-info-value">{{ crimeIndex.toFixed(1) }}</span>
          </div>
          
          <div v-if="comparisonReference" class="risk-info-item comparison-ref">
            <span class="risk-info-label">Compared to:</span>
            <span class="risk-info-value">{{ comparisonReference.name || comparisonReference.code }}</span>
          </div>
        </div>
      </div>
      
      <!-- Crime Statistics -->
      <div v-if="hasSelectedCrimeData" class="crime-section">
        <div class="section-header-with-info">
          <h4>Crime Statistics ({{ dataYear }})</h4>
          <div class="info-tooltip-container">
            <svg class="info-icon-small" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <div class="info-tooltip">
              <strong>Crime Index Values</strong>
              <p>Index values represent crime rates relative to national averages (100 = national average). Higher values indicate higher crime rates.</p>
            </div>
          </div>
        </div>
        <div class="crime-table-container">
          <table class="crime-table" role="table" :aria-label="`Crime statistics for ${displayName}`">
            <thead>
              <tr>
                <th scope="col">Crime Type</th>
                <th scope="col">Index Value</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="crimeType in displayedCrimeTypes" 
                :key="crimeType.key"
              >
                <td class="crime-type">
                  <span class="crime-label">{{ crimeType.label }}</span>
                </td>
                <td class="crime-value">
                  <span class="value-number">{{ formatCrimeValue(crimeData[crimeType.key]) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- Crime Intensity Summary -->
        <div class="intensity-summary">
          <div class="intensity-bar-container">
            <div class="label-with-tooltip">
              <label class="intensity-label">{{ getIntensityLabel() }}:</label>
              <div class="info-tooltip-container">
                <svg class="info-icon-tiny" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <div class="info-tooltip">
                  <strong>{{ getIntensityTooltipTitle() }}</strong>
                  <p>{{ getIntensityTooltipText() }}</p>
                </div>
              </div>
            </div>
            <div class="intensity-bar" :aria-label="`Crime intensity: ${getDisplayedIntensityPercent()}%`">
              <div 
                class="intensity-fill" 
                :style="{ width: getDisplayedIntensityPercent() + '%' }"
                :class="getIntensityClass(getDisplayedIntensityPercent())"
              ></div>
            </div>
            <span class="intensity-text">{{ getDisplayedIntensityPercent() }}%</span>
          </div>
          <div class="combined-value">
            <span class="label">Risk Level:</span>
            <span 
              class="value risk-badge"
              :class="getRiskBadgeClass(riskLevel || getRiskLevelFromPercent(getDisplayedIntensityPercent()))"
            >
              {{ formatRiskLevel(riskLevel || getRiskLevelFromPercent(getDisplayedIntensityPercent())) }}
            </span>
          </div>
        </div>
      </div>
      
      <!-- No Crime Filters Selected Message -->
      <div v-else-if="selectedCrimeTypes.length === 0" class="no-filters-section">
        <div class="no-filters-message">
          <svg class="info-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <p>Select crime types from the sidebar to view crime statistics for this area.</p>
          <div class="filter-hint">
            <small>Use the Crime Data toggle and select specific crime types to analyze.</small>
          </div>
        </div>
      </div>
      
      <!-- No Crime Data Available Message -->
      <div v-else-if="selectedCrimeTypes.length > 0 && !hasSelectedCrimeData" class="no-crime-section">
        <div class="no-data-message">
          <svg class="no-data-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <p>No crime data available for the selected crime types in this area.</p>
        </div>
      </div>
      
      <!-- Debug Information (Development Only) -->
      <div v-if="isDevelopment && (crimeIntensityDetails || hasSelectedCrimeData)" class="debug-section">
        <h4>🔍 Color Calculation Debug</h4>
        
        <!-- Step 1: Raw Data -->
        <div class="debug-subsection">
          <h5>Step 1: Raw Crime Data</h5>
          <div class="debug-info">
            <div v-for="crimeType in displayedCrimeTypes" :key="crimeType.key" class="debug-item">
              <span class="label">{{ crimeType.label }}:</span>
              <span class="value">{{ crimeData[crimeType.key] || 'N/A' }}</span>
            </div>
            <div v-if="displayedCrimeTypes.length > 1" class="debug-item highlight">
              <span class="label">Average Value:</span>
              <span class="value">{{ crimeIntensityDetails ? crimeIntensityDetails.averageValue.toFixed(2) : 'N/A' }}</span>
            </div>
          </div>
        </div>

        <!-- Step 2: Comparison Range -->
        <div v-if="debugRangeData" class="debug-subsection">
          <h5>Step 2: {{ getComparisonLabel(debugRangeData) }}</h5>
          <div class="debug-info">
            <div class="debug-item">
              <span class="label">Min Index ({{ getComparisonShortLabel(debugRangeData) }}):</span>
              <span class="value">{{ debugRangeData.min_index !== undefined ? debugRangeData.min_index.toFixed(1) : 'N/A' }}</span>
            </div>
            <div class="debug-item">
              <span class="label">Max Index ({{ getComparisonShortLabel(debugRangeData) }}):</span>
              <span class="value">{{ debugRangeData.max_index !== undefined ? debugRangeData.max_index.toFixed(1) : 'N/A' }}</span>
            </div>
            <div v-if="debugRangeData.mean_index" class="debug-item">
              <span class="label">Mean Index:</span>
              <span class="value">{{ debugRangeData.mean_index.toFixed(1) }}</span>
            </div>
            <div v-if="debugRangeData.median_index" class="debug-item">
              <span class="label">Median Index:</span>
              <span class="value">{{ debugRangeData.median_index.toFixed(1) }}</span>
            </div>
            <div class="debug-item">
              <span class="label">Range Size:</span>
              <span class="value">{{ debugRangeData.max_index !== undefined && debugRangeData.min_index !== undefined ? (debugRangeData.max_index - debugRangeData.min_index).toFixed(1) : 'N/A' }}</span>
            </div>
            <div class="debug-item">
              <span class="label">{{ getEntityCountLabel(debugRangeData) }}:</span>
              <span class="value">{{ debugRangeData.total_entities || 'N/A' }}</span>
            </div>
          </div>
        </div>

        <!-- Step 3: Normalize to Percentage -->
        <div v-if="debugRangeData && crimeIndex !== null" class="debug-subsection">
          <h5>Step 3: Normalize to Percentage</h5>
          <div class="debug-info">
            <div class="debug-item">
              <span class="label">📊 Normalization Formula:</span>
              <span class="value formula">((Index - Min) / (Max - Min)) × 100</span>
            </div>
            
            <!-- Detailed Calculation -->
            <div class="debug-item">
              <span class="label">This Area's Index:</span>
              <span class="value formula highlight-value">{{ crimeIndex.toFixed(1) }}</span>
            </div>
            
            <div class="debug-item">
              <span class="label">Min Index ({{ getComparisonGroupName() }}):</span>
              <span class="value">{{ debugRangeData.min_index !== undefined ? debugRangeData.min_index.toFixed(1) : 'N/A' }}</span>
            </div>
            
            <div class="debug-item">
              <span class="label">Max Index ({{ getComparisonGroupName() }}):</span>
              <span class="value">{{ debugRangeData.max_index !== undefined ? debugRangeData.max_index.toFixed(1) : 'N/A' }}</span>
            </div>
            
            <div v-if="debugRangeData.mean_index" class="debug-item">
              <span class="label">Mean Index:</span>
              <span class="value">{{ debugRangeData.mean_index.toFixed(1) }}</span>
            </div>
            
            <div v-if="debugRangeData.median_index" class="debug-item">
              <span class="label">Median Index:</span>
              <span class="value">{{ debugRangeData.median_index.toFixed(1) }}</span>
            </div>
            
            <div class="debug-item">
              <span class="label">Total Features:</span>
              <span class="value">{{ debugRangeData.total_entities || 'N/A' }}</span>
            </div>
            
            <!-- Calculation Steps -->
            <div class="debug-item calculation-step">
              <span class="label">Step 3a: Subtract Min</span>
              <span class="value formula">{{ crimeIndex.toFixed(1) }} - {{ debugRangeData.min_index !== undefined ? debugRangeData.min_index.toFixed(1) : 0 }} = {{ (crimeIndex - (debugRangeData.min_index || 0)).toFixed(1) }}</span>
            </div>
            
            <div class="debug-item calculation-step">
              <span class="label">Step 3b: Calculate Range</span>
              <span class="value formula">{{ debugRangeData.max_index !== undefined ? debugRangeData.max_index.toFixed(1) : 0 }} - {{ debugRangeData.min_index !== undefined ? debugRangeData.min_index.toFixed(1) : 0 }} = {{ ((debugRangeData.max_index || 0) - (debugRangeData.min_index || 0)).toFixed(1) }}</span>
            </div>
            
            <div class="debug-item calculation-step">
              <span class="label">Step 3c: Divide and Multiply</span>
              <span class="value formula">({{ (crimeIndex - (debugRangeData.min_index || 0)).toFixed(1) }} / {{ ((debugRangeData.max_index || 0) - (debugRangeData.min_index || 0)).toFixed(1) }}) × 100 = {{ normalizedScore !== null ? normalizedScore.toFixed(1) : 'N/A' }}%</span>
            </div>
            
            <!-- Result -->
            <div class="debug-item highlight">
              <span class="label">✓ Normalized Score:</span>
              <span class="value">{{ normalizedScore !== null ? normalizedScore.toFixed(1) : 'N/A' }}%</span>
            </div>
            
            <!-- Interpretation -->
            <div class="debug-item interpretation">
              <span class="label">📖 What This Means:</span>
              <span class="value formula">
                This area's crime index is {{ normalizedScore !== null ? normalizedScore.toFixed(1) : 0 }}% of the way
                from the lowest ({{ debugRangeData.min_index !== undefined ? debugRangeData.min_index.toFixed(1) : 'N/A' }})
                to the highest ({{ debugRangeData.max_index !== undefined ? debugRangeData.max_index.toFixed(1) : 'N/A' }})
                crime index in {{ getComparisonGroupName() }}.
              </span>
            </div>
            
            <!-- Additional Context -->
            <div class="debug-item interpretation">
              <span class="label">🎯 Position:</span>
              <span class="value formula">
                {{ getIndexPositionDescription() }}
              </span>
            </div>
          </div>
        </div>

        <!-- Step 4: Map to Risk Level -->
        <div v-if="normalizedScore !== null" class="debug-subsection">
          <h5>Step 4: Map to Risk Level</h5>
          <div class="debug-info">
            <div class="debug-item">
              <span class="label">Thresholds:</span>
              <span class="value formula">0-25% Low, 25-50% Mod, 50-75% Elev, 75-100% High</span>
            </div>
            <div class="debug-item highlight">
              <span class="label">Normalized Score:</span>
              <span class="value">{{ normalizedScore.toFixed(1) }}%</span>
            </div>
            <div class="debug-item highlight">
              <span class="label">Risk Level:</span>
              <span class="value">{{ formatRiskLevel(riskLevel) || getRiskLevelFromPercent(normalizedScore) }}</span>
            </div>
          </div>
        </div>

        <!-- Step 5: Color Assignment -->
        <div v-if="riskLevel" class="debug-subsection">
          <h5>Step 5: Assign Color</h5>
          <div class="debug-info">
            <div class="debug-item">
              <span class="label">Color Map:</span>
              <span class="value formula">Low=Green, Mod=Yellow, Elev=Orange, High=Red</span>
            </div>
            <div class="debug-item highlight">
              <span class="label">Risk Level Color:</span>
              <span class="value color-swatch" :style="{ backgroundColor: getRiskLevelColor(riskLevel), color: 'white' }">
                {{ getRiskLevelColor(riskLevel) }}
              </span>
            </div>
            <div v-if="polygonColor" class="debug-item">
              <span class="label">Actual Polygon Color:</span>
              <span class="value color-swatch" :style="{ backgroundColor: polygonColor, color: 'white' }">
                {{ polygonColor }}
              </span>
            </div>
          </div>
        </div>

        <!-- Comparison Mode Data -->
        <div v-if="riskLevel && comparisonMode !== 'viewport'" class="debug-subsection">
          <h5>📊 Comparison Mode Data</h5>
          <div class="debug-info">
            <div class="debug-item">
              <span class="label">Mode:</span>
              <span class="value">{{ comparisonMode }}</span>
            </div>
            <div class="debug-item">
              <span class="label">Risk Level (Backend):</span>
              <span class="value">{{ riskLevel }}</span>
            </div>
            <div v-if="riskPercentile !== null" class="debug-item">
              <span class="label">Percentile (Backend):</span>
              <span class="value">{{ riskPercentile }}%</span>
            </div>
            <div v-if="perCapitaRate !== null" class="debug-item">
              <span class="label">Per Capita Rate:</span>
              <span class="value">{{ perCapitaRate.toFixed(2) }}/1000</span>
            </div>
            <div v-if="comparisonReference" class="debug-item">
              <span class="label">Reference:</span>
              <span class="value">{{ comparisonReference.name || comparisonReference.code }}</span>
            </div>
          </div>
        </div>

        <!-- Summary -->
        <div v-if="normalizedScore !== null && riskLevel" class="debug-summary">
          <strong>Summary:</strong> This {{ getUnitTypeLabel(unitType).toLowerCase() }} has a crime index of 
          <strong>{{ crimeIndex !== null ? crimeIndex.toFixed(1) : 'N/A' }}</strong>, 
          which normalizes to <strong>{{ normalizedScore.toFixed(1) }}%</strong> 
          {{ getComparisonSummaryText() }}, placing it in the 
          <strong>{{ formatRiskLevel(riskLevel) }}</strong> 
          risk category, which is colored 
          <strong :style="{ color: getRiskLevelColor(riskLevel) }">
            {{ getColorName(getRiskLevelColor(riskLevel)) }}
          </strong>.
        </div>
      </div>

      <!-- Information Footer -->
      <div class="info-section">
        <p class="data-note">
          <small>
            <span v-if="hasSelectedCrimeData">
              Crime indices are relative measures compared to national averages. 
              Higher values indicate higher risk levels. Data is aggregated at the {{ getUnitTypeLabel(unitType).toLowerCase() }} level.
            </span>
            <span v-else>
              Click on geographic areas to view detailed information and crime statistics.
            </span>
          </small>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CrimeDataPopup',
  
  props: {
    // Geographic unit information
    unitCode: {
      type: String,
      required: true
    },
    unitType: {
      type: String,
      required: true,
      validator: value => ['states', 'counties', 'zips', 'tracts', 'block_group', 'block'].includes(value)
    },
    unitName: {
      type: String,
      default: ''
    },
    
    // Crime data
    crimeData: {
      type: Object,
      default: () => ({})
    },
    
    // Population data
    populationData: {
      type: Number,
      default: null
    },
    
    // Additional demographic data
    populationDensity: {
      type: Number,
      default: null
    },
    
    householdCount: {
      type: Number,
      default: null
    },
    
    // Geographic hierarchy data
    stateCode: {
      type: String,
      default: null
    },
    
    stateName: {
      type: String,
      default: null
    },
    
    countyCode: {
      type: String,
      default: null
    },
    
    countyName: {
      type: String,
      default: null
    },
    
    tractCode: {
      type: String,
      default: null
    },
    
    blockGroupCode: {
      type: String,
      default: null
    },
    
    // Data year
    dataYear: {
      type: Number,
      default: 2025
    },
    
    // Selected crime types for filtering
    selectedCrimeTypes: {
      type: Array,
      default: () => []
    },
    
    // Available crime types with labels
    availableCrimeTypes: {
      type: Array,
      default: () => []
    },
    
    // Crime intensity calculation
    combinedValue: {
      type: Number,
      default: 0
    },
    
    intensityPercent: {
      type: Number,
      default: 0
    },
    
    // Responsive design
    isMobile: {
      type: Boolean,
      default: false
    },
    
    // Control popup visibility
    show: {
      type: Boolean,
      default: true
    },

    // Debug information (development only)
    crimeIntensityDetails: {
      type: Object,
      default: null
    },

    polygonColor: {
      type: String,
      default: null
    },

    // Risk comparison data
    riskLevel: {
      type: String,
      default: null
    },

    normalizedScore: {
      type: Number,
      default: null
    },

    crimeIndex: {
      type: Number,
      default: null
    },

    riskPercentile: {
      type: Number,
      default: null
    },

    perCapitaRate: {
      type: Number,
      default: null
    },

    comparisonMode: {
      type: String,
      default: 'viewport',
      validator: value => ['viewport', 'state', 'county'].includes(value)
    },

    comparisonReference: {
      type: Object,
      default: null
    }
  },
  
  computed: {
    shouldShowPopup() {
      // Always show popup if we have geographic information
      // Now we always show useful information about the geographic unit
      return this.show && this.hasBasicInfo;
    },
    
    hasBasicInfo() {
      // Show popup if we have basic geographic information
      return this.unitCode && this.unitType;
    },
    
    displayName() {
      if (this.unitName && this.unitName.trim()) {
        return this.unitName;
      }
      
      // Generate a friendly name based on unit type and code
      const typeLabel = this.getUnitTypeLabel(this.unitType);
      return `${typeLabel} ${this.unitCode}`;
    },
    
    hasSelectedCrimeData() {
      return this.selectedCrimeTypes.length > 0 && 
             this.crimeData && 
             Object.keys(this.crimeData).length > 0 &&
             this.displayedCrimeTypes.some(type => this.crimeData[type.key] !== undefined);
    },
    
    displayedCrimeTypes() {
      return this.availableCrimeTypes.filter(crimeType => 
        this.selectedCrimeTypes.includes(crimeType.value)
      ).map(crimeType => ({
        key: crimeType.value,
        label: crimeType.label
      }));
    },

    /**
     * Get actual population from crime_data if available
     */
    actualPopulation() {
      // Try to get population from crime_data object
      if (this.crimeData && this.crimeData.population) {
        return this.crimeData.population;
      }
      
      // Try to get from crime_data.population_cy
      if (this.crimeData && this.crimeData.population_cy) {
        return this.crimeData.population_cy;
      }
      
      return null;
    },
    
    hasPopulationEstimate() {
      return this.estimatedPopulation && this.estimatedPopulation > 0;
    },
    
    estimatedPopulation() {
      // Don't show estimate if we have actual data
      if (this.populationData) return null;
      
      // Generate a more realistic estimate based on the unit code
      // This creates variation while being deterministic
      const hash = this.simpleHash(this.unitCode);
      
      if (this.unitType === 'states') {
        // States typically range from 500,000 to 40,000,000 people
        return 500000 + (hash % 39500000);
      } else if (this.unitType === 'counties') {
        // Counties typically range from 1,000 to 10,000,000 people
        return 1000 + (hash % 9999000);
      } else if (this.unitType === 'zips') {
        // ZIP codes typically range from 0 to 100,000 people
        return hash % 100000;
      } else if (this.unitType === 'tracts') {
        // Census tracts typically range from 1,200 to 8,000 people
        return 1200 + (hash % 6800);
      } else if (this.unitType === 'block_group') {
        // Block groups typically range from 600 to 3,000 people
        return 600 + (hash % 2400);
      } else if (this.unitType === 'block') {
        // Blocks typically range from 0 to 400 people
        return hash % 400;
      }
      
      return null;
    },

    isDevelopment() {
      return import.meta.env.DEV;
    },

    /**
     * Computed property for debug range data
     * Caches the result and provides debugging
     */
    debugRangeData() {
      // Log what we have for debugging
      if (this.isDevelopment) {
        console.log('🔍 Debug Range Data Check:', {
          hasComparisonReference: !!this.comparisonReference,
          comparisonReference: this.comparisonReference,
          hasCrimeIntensityDetails: !!this.crimeIntensityDetails,
          crimeIntensityDetails: this.crimeIntensityDetails,
          comparisonMode: this.comparisonMode,
          comparisonModeType: typeof this.comparisonMode,
          isStateMode: this.comparisonMode === 'state',
          isCountyMode: this.comparisonMode === 'county',
          crimeIndex: this.crimeIndex,
          normalizedScore: this.normalizedScore
        });
      }

      // Priority 1: Use comparisonReference from backend when in state/county mode
      // This contains the correct state/county-level statistics
      if (this.comparisonReference && (this.comparisonMode === 'state' || this.comparisonMode === 'county')) {
        if (this.isDevelopment) {
          console.log('✅ Using comparisonReference from backend:', {
            type: this.comparisonReference.type,
            code: this.comparisonReference.code,
            min_index: this.comparisonReference.min_index,
            max_index: this.comparisonReference.max_index,
            mean_index: this.comparisonReference.mean_index,
            median_index: this.comparisonReference.median_index,
            total_entities: this.comparisonReference.total_entities
          });
        }
        return this.comparisonReference;
      }
      
      // Priority 2: Use crimeIntensityDetails.range for viewport mode
      // This is calculated from visible features in the viewport
      if (this.crimeIntensityDetails && this.crimeIntensityDetails.range) {
        if (this.isDevelopment) {
          console.log('⚠️ Falling back to crimeIntensityDetails.range (viewport mode):', this.crimeIntensityDetails.range);
        }
        const range = this.crimeIntensityDetails.range;
        // Transform to consistent format with _index suffix (new format)
        return {
          type: range.type || 'viewport',
          code: range.code,
          min_index: range.min !== undefined ? range.min : (range.min_index !== undefined ? range.min_index : range.min_rate),
          max_index: range.max !== undefined ? range.max : (range.max_index !== undefined ? range.max_index : range.max_rate),
          mean_index: range.mean !== undefined ? range.mean : range.mean_index,
          median_index: range.median !== undefined ? range.median : (range.median_index !== undefined ? range.median_index : range.median_rate),
          total_entities: range.count !== undefined ? range.count : range.total_entities
        };
      }
      
      // Priority 3: Fallback to comparisonReference if available (viewport mode without crimeIntensityDetails)
      if (this.comparisonReference) {
        return this.comparisonReference;
      }
      
      return null;
    }
  },
  
  methods: {
    getUnitTypeLabel(unitType) {
      const labels = {
        'states': 'State',
        'counties': 'County',
        'zips': 'ZIP Code',
        'tracts': 'Census Tract',
        'block_group': 'Block Group',
        'block': 'Block'
      };
      return labels[unitType] || 'Geographic Unit';
    },
    
    formatNumber(number) {
      if (number === null || number === undefined) {
        return 'N/A';
      }
      
      return new Intl.NumberFormat('en-US').format(number);
    },
    
    formatCrimeValue(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return 'N/A';
      }
      
      return Math.round(value);
    },
    
    getRiskClass(value) {
      const riskLevel = this.getRiskLevel(value);
      return `risk-${riskLevel}`;
    },
    
    getRiskLabel(value) {
      const riskLevel = this.getRiskLevel(value);
      const labels = {
        'very_low': 'Very Low',
        'low': 'Low',
        'low_moderate': 'Low-Moderate',
        'moderate': 'Moderate',
        'moderate_high': 'Moderate-High',
        'high': 'High',
        'very_high': 'Very High',
        'unknown': 'Unknown',
        // Legacy support
        'elevated': 'Elevated'
      };
      return labels[riskLevel] || 'Unknown';
    },
    
    getRiskLevel(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return 'unknown';
      }
      
      // Use viewport-based risk calculation if crime intensity details are available
      if (this.crimeIntensityDetails && this.crimeIntensityDetails.intensityPercent !== undefined) {
        const percent = this.crimeIntensityDetails.intensityPercent;
        
        // 7-level risk thresholds consistent with map color scheme
        // Very Low: 0-14%, Low: 14-28%, Low-Moderate: 28-42%
        // Moderate: 42-57%, Moderate-High: 57-71%, High: 71-85%, Very High: 85-100%
        if (percent < 14) {
          return 'very_low';
        } else if (percent < 28) {
          return 'low';
        } else if (percent < 42) {
          return 'low_moderate';
        } else if (percent < 57) {
          return 'moderate';
        } else if (percent < 71) {
          return 'moderate_high';
        } else if (percent < 85) {
          return 'high';
        } else {
          return 'very_high';
        }
      }
      
      // Fallback to fixed thresholds if no viewport data available
      // Using 7-level classification based on crime index values
      const numValue = parseFloat(value);
      
      // Risk thresholds based on crime index values
      // These are relative to national averages (100 = national average)
      if (numValue < 50) {
        return 'very_low';
      } else if (numValue < 75) {
        return 'low';
      } else if (numValue < 100) {
        return 'low_moderate';
      } else if (numValue < 125) {
        return 'moderate';
      } else if (numValue < 150) {
        return 'moderate_high';
      } else if (numValue < 175) {
        return 'high';
      } else {
        return 'very_high';
      }
    },
    
    isHighRisk(value) {
      const riskLevel = this.getRiskLevel(value);
      // Consider both 'high' and 'very_high' as high risk for highlighting
      return riskLevel === 'high' || riskLevel === 'very_high';
    },
    
    getIntensityClass(percent) {
      // Use 7-level color system matching the map colors
      if (percent < 14) {
        return 'intensity-very-low';      // Dark green
      } else if (percent < 28) {
        return 'intensity-low';           // Green
      } else if (percent < 42) {
        return 'intensity-low-moderate';  // Yellow-green
      } else if (percent < 57) {
        return 'intensity-moderate';      // Yellow
      } else if (percent < 71) {
        return 'intensity-moderate-high'; // Orange
      } else if (percent < 85) {
        return 'intensity-high';          // Red
      } else {
        return 'intensity-very-high';     // Dark red
      }
    },

    getFixedRiskLevel(value) {
      if (value === null || value === undefined || isNaN(value)) {
        return 'unknown';
      }
      
      const numValue = parseFloat(value);
      
      // Original fixed thresholds for comparison
      if (numValue < 75) {
        return 'low';
      } else if (numValue < 125) {
        return 'medium';
      } else {
        return 'high';
      }
    },
    
    simpleHash(str) {
      // Simple hash function to generate consistent numbers from strings
      let hash = 0;
      if (!str || str.length === 0) return hash;
      
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
      }
      
      return Math.abs(hash);
    },
    
    getStateName(stateCode) {
      // Common state codes - you can expand this or fetch from an API
      const stateNames = {
        '01': 'Alabama', '02': 'Alaska', '04': 'Arizona', '05': 'Arkansas',
        '06': 'California', '08': 'Colorado', '09': 'Connecticut', '10': 'Delaware',
        '11': 'District of Columbia', '12': 'Florida', '13': 'Georgia', '15': 'Hawaii',
        '16': 'Idaho', '17': 'Illinois', '18': 'Indiana', '19': 'Iowa',
        '20': 'Kansas', '21': 'Kentucky', '22': 'Louisiana', '23': 'Maine',
        '24': 'Maryland', '25': 'Massachusetts', '26': 'Michigan', '27': 'Minnesota',
        '28': 'Mississippi', '29': 'Missouri', '30': 'Montana', '31': 'Nebraska',
        '32': 'Nevada', '33': 'New Hampshire', '34': 'New Jersey', '35': 'New Mexico',
        '36': 'New York', '37': 'North Carolina', '38': 'North Dakota', '39': 'Ohio',
        '40': 'Oklahoma', '41': 'Oregon', '42': 'Pennsylvania', '44': 'Rhode Island',
        '45': 'South Carolina', '46': 'South Dakota', '47': 'Tennessee', '48': 'Texas',
        '49': 'Utah', '50': 'Vermont', '51': 'Virginia', '53': 'Washington',
        '54': 'West Virginia', '55': 'Wisconsin', '56': 'Wyoming'
      };
      
      return this.stateName || stateNames[stateCode] || `State ${stateCode}`;
    },
    
    getCountyName(countyCode) {
      // Return provided county name or null to show generic format
      return this.countyName;
    },
    
    formatTractCode(tractCode) {
      if (!tractCode) return '';
      
      // Format tract code for better readability
      // Typical format: SSCCCTTTTTT -> SS-CCC-TTTTTT
      if (tractCode.length >= 11) {
        const state = tractCode.substring(0, 2);
        const county = tractCode.substring(2, 5);
        const tract = tractCode.substring(5);
        return `${state}-${county}-${tract}`;
      }
      
      return tractCode;
    },
    
    formatBlockGroupCode(blockGroupCode) {
      if (!blockGroupCode) return '';
      
      // Format block group code for better readability
      // Typical format: SSCCCTTTTTTB -> SS-CCC-TTTTTT-B
      if (blockGroupCode.length >= 12) {
        const state = blockGroupCode.substring(0, 2);
        const county = blockGroupCode.substring(2, 5);
        const tract = blockGroupCode.substring(5, 11);
        const blockGroup = blockGroupCode.substring(11);
        return `${state}-${county}-${tract}-${blockGroup}`;
      }
      
      return blockGroupCode;
    },

    formatRiskLevel(riskLevel) {
      const labels = {
        'very_low': 'Very Low',
        'low': 'Low',
        'low_moderate': 'Low-Moderate',
        'moderate': 'Moderate',
        'moderate_high': 'Moderate-High',
        'high': 'High',
        'very_high': 'Very High',
        'no_data': 'No Data'
      };
      return labels[riskLevel] || riskLevel;
    },

    getRiskBadgeClass(riskLevel) {
      return `risk-badge-${riskLevel}`;
    },

    getComparisonModeTitle() {
      if (this.comparisonMode === 'state') {
        return 'State-Level Comparison';
      } else if (this.comparisonMode === 'county') {
        return 'County-Level Comparison';
      } else {
        return 'Viewport Comparison';
      }
    },

    getComparisonModeExplanation() {
      if (this.comparisonMode === 'state') {
        return 'Risk level is calculated by comparing this area to all similar geographic units within the same state. This shows how this area ranks relative to the entire state.';
      } else if (this.comparisonMode === 'county') {
        return 'Risk level is calculated by comparing this area to all similar geographic units within the same county. This provides a localized comparison within the county boundaries.';
      } else {
        return 'Risk level is calculated by comparing this area to all visible areas on the current map view. Zoom or pan to change the comparison group.';
      }
    },

    getRiskLevelFromPercent(percent) {
      // 7-level risk classification
      if (percent < 14) return 'Very Low';
      else if (percent < 28) return 'Low';
      else if (percent < 42) return 'Low-Moderate';
      else if (percent < 57) return 'Moderate';
      else if (percent < 71) return 'Moderate-High';
      else if (percent < 85) return 'High';
      else return 'Very High';
    },

    getComparisonLabel(range) {
      if (!range || !range.type) {
        return 'Viewport Range';
      }
      
      if (range.type === 'viewport') {
        return 'Viewport Range';
      } else if (range.type === 'state') {
        return `State Comparison (${range.code || 'N/A'})`;
      } else if (range.type === 'county') {
        return `County Comparison (${range.code || 'N/A'})`;
      }
      
      return 'Comparison Range';
    },

    getComparisonShortLabel(range) {
      if (!range || !range.type) {
        return 'Viewport';
      }
      
      if (range.type === 'viewport') {
        return 'Viewport';
      } else if (range.type === 'state') {
        return `State ${range.code || ''}`;
      } else if (range.type === 'county') {
        return `County ${range.code || ''}`;
      }
      
      return 'Comparison';
    },

    getEntityCountLabel(range) {
      if (!range || !range.type) {
        return 'Features Count';
      }
      
      if (range.type === 'viewport') {
        return 'Visible Features';
      } else if (range.type === 'state') {
        return 'State Total';
      } else if (range.type === 'county') {
        return 'County Total';
      }
      
      return 'Total Features';
    },

    /**
     * Calculate number of features below this area's crime rate
     */
    calculateFeaturesBelow() {
      if (!this.riskPercentile || !this.debugRangeData || !this.debugRangeData.total_entities) {
        return 'N/A';
      }
      
      const totalFeatures = this.debugRangeData.total_entities;
      const percentile = this.riskPercentile;
      const featuresBelow = Math.round((percentile / 100) * totalFeatures);
      
      return featuresBelow;
    },

    /**
     * Calculate number of features above this area's crime rate
     */
    calculateFeaturesAbove() {
      if (!this.riskPercentile || !this.debugRangeData || !this.debugRangeData.total_entities) {
        return 'N/A';
      }
      
      const totalFeatures = this.debugRangeData.total_entities;
      const featuresBelow = this.calculateFeaturesBelow();
      
      if (featuresBelow === 'N/A') return 'N/A';
      
      return totalFeatures - featuresBelow;
    },

    /**
     * Calculate the ratio for percentile calculation
     */
    calculateRatio() {
      if (!this.riskPercentile || !this.debugRangeData || !this.debugRangeData.total_entities) {
        return 'N/A';
      }
      
      const featuresBelow = this.calculateFeaturesBelow();
      const totalFeatures = this.debugRangeData.total_entities;
      
      if (featuresBelow === 'N/A') return 'N/A';
      
      return (featuresBelow / totalFeatures).toFixed(4);
    },

    /**
     * Get comparison group name for display
     */
    getComparisonGroupName() {
      if (!this.debugRangeData || !this.debugRangeData.type) {
        return 'comparison group';
      }
      
      if (this.debugRangeData.type === 'viewport') {
        return 'viewport';
      } else if (this.debugRangeData.type === 'state') {
        const stateCode = this.debugRangeData.code || '';
        return `${this.getStateName(stateCode)}`;
      } else if (this.debugRangeData.type === 'county') {
        return `county ${this.debugRangeData.code || ''}`;
      }
      
      return 'comparison group';
    },

    /**
     * Get position description relative to median
     */
    getPositionDescription() {
      if (!this.perCapitaRate || !this.debugRangeData) {
        return 'N/A';
      }
      
      const rate = this.perCapitaRate;
      const min = this.debugRangeData.min_rate;
      const max = this.debugRangeData.max_rate;
      const median = this.debugRangeData.median_rate;
      
      if (min === undefined || max === undefined) {
        return 'N/A';
      }
      
      let position = '';
      
      if (median !== undefined) {
        if (rate < median) {
          const diff = ((median - rate) / median * 100).toFixed(0);
          position = `${diff}% below median (safer than average)`;
        } else if (rate > median) {
          const diff = ((rate - median) / median * 100).toFixed(0);
          position = `${diff}% above median (more dangerous than average)`;
        } else {
          position = 'At median (average)';
        }
      } else {
        // If no median, describe position in range
        const rangePosition = ((rate - min) / (max - min) * 100).toFixed(0);
        position = `${rangePosition}% through the range from min to max`;
      }
      
      return position;
    },

    getColorName(hexColor) {
      const colorNames = {
        '#22c55e': 'Green',
        '#eab308': 'Yellow',
        '#f97316': 'Orange',
        '#ef4444': 'Red',
        '#9ca3af': 'Gray'
      };
      return colorNames[hexColor] || hexColor;
    },

    /**
     * Get debug range data from either crimeIntensityDetails or comparisonReference
     * This ensures the debug panel shows the correct min/max values
     */
    getDebugRangeData() {
      // First, try to use crimeIntensityDetails.range if available (old format)
      if (this.crimeIntensityDetails && this.crimeIntensityDetails.range) {
        return this.crimeIntensityDetails.range;
      }
      
      // Otherwise, use comparisonReference from backend (new format)
      if (this.comparisonReference) {
        return this.comparisonReference;
      }
      
      return null;
    },

    /**
     * Get the color for a risk level (7-level system)
     */
    getRiskLevelColor(riskLevel) {
      const colorMap = {
        'very_low': '#059669',
        'low': '#22c55e',
        'low_moderate': '#84cc16',
        'moderate': '#eab308',
        'moderate_high': '#f97316',
        'high': '#ef4444',
        'very_high': '#dc2626',
        'no_data': '#9ca3af'
      };
      return colorMap[riskLevel] || '#9ca3af';
    },

    /**
     * Get comparison summary text for debug panel
     */
    getComparisonSummaryText() {
      if (this.comparisonMode === 'state') {
        const stateCode = this.comparisonReference?.code || '';
        return `within ${this.getStateName(stateCode)}`;
      } else if (this.comparisonMode === 'county') {
        const countyCode = this.comparisonReference?.code || '';
        return `within county ${countyCode}`;
      } else {
        return 'in the current viewport';
      }
    },

    /**
     * Get index position description
     */
    getIndexPositionDescription() {
      if (!this.crimeIndex || !this.debugRangeData) {
        return 'N/A';
      }
      
      const index = this.crimeIndex;
      const min = this.debugRangeData.min_index;
      const max = this.debugRangeData.max_index;
      const median = this.debugRangeData.median_index;
      
      if (min === undefined || max === undefined) {
        return 'N/A';
      }
      
      let description = '';
      
      if (median !== undefined) {
        if (index < median) {
          const diff = ((median - index) / median * 100).toFixed(0);
          description = `${diff}% below median (lower crime than average)`;
        } else if (index > median) {
          const diff = ((index - median) / median * 100).toFixed(0);
          description = `${diff}% above median (higher crime than average)`;
        } else {
          description = 'At median (average crime level)';
        }
      } else {
        const rangePosition = ((index - min) / (max - min) * 100).toFixed(0);
        description = `${rangePosition}% through the range from min to max`;
      }
      
      return description;
    },

    /**
     * Get the label for the intensity bar based on comparison mode
     */
    getIntensityLabel() {
      if (this.comparisonMode === 'state' && this.comparisonReference) {
        return 'Position in State';
      } else if (this.comparisonMode === 'county' && this.comparisonReference) {
        return 'Position in County';
      } else {
        return 'Position in Viewport';
      }
    },

    /**
     * Get the tooltip title for the intensity bar
     */
    getIntensityTooltipTitle() {
      if (this.comparisonMode === 'state' && this.comparisonReference) {
        return 'State Comparison';
      } else if (this.comparisonMode === 'county' && this.comparisonReference) {
        return 'County Comparison';
      } else {
        return 'Viewport Comparison';
      }
    },

    /**
     * Get the tooltip text for the intensity bar
     */
    getIntensityTooltipText() {
      if (this.comparisonMode === 'state' && this.comparisonReference) {
        return 'Shows where this area\'s crime index falls within the state range. 0% = lowest in state, 100% = highest in state.';
      } else if (this.comparisonMode === 'county' && this.comparisonReference) {
        return 'Shows where this area\'s crime index falls within the county range. 0% = lowest in county, 100% = highest in county.';
      } else {
        return 'Shows where this area\'s crime index falls within visible areas on the map. 0% = lowest in viewport, 100% = highest in viewport.';
      }
    },

    /**
     * Get the correct intensity percentage to display
     * Always uses normalizedScore from backend when available (all modes)
     */
    getDisplayedIntensityPercent() {
      // Debug logging
      if (this.isDevelopment) {
        console.log('🎯 Intensity Percent Calculation:', {
          comparisonMode: this.comparisonMode,
          normalizedScore: this.normalizedScore,
          intensityPercent: this.intensityPercent,
          hasComparisonReference: !!this.comparisonReference,
          crimeIndex: this.crimeIndex
        });
      }

      // Always use normalizedScore from backend when available (viewport, state, or county mode)
      // Backend calculates this correctly for all comparison modes
      if (this.normalizedScore !== null && this.normalizedScore !== undefined) {
        return Math.round(this.normalizedScore);
      }
      
      // Fallback to intensityPercent only if normalizedScore is not available
      return this.intensityPercent || 0;
    }
  }
}
</script>

<style scoped>
.crime-popup {
  max-width: 380px;
  min-width: 300px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  line-height: 1.5;
}

.crime-popup.mobile {
  max-width: 280px;
  min-width: 250px;
}

/* Header Styles */
.popup-header {
  background: #f8f9fa;
  padding: 16px;
  border-bottom: 1px solid #e9ecef;
}

.unit-name {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #212529;
  line-height: 1.3;
  word-wrap: break-word;
}

.unit-details {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin: 0;
  font-size: 14px;
  color: #6c757d;
}

.unit-type {
  background: #e3f2fd;
  color: #1976d2;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.unit-code {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  background: #f1f3f4;
  padding: 2px 6px;
  border-radius: 4px;
}

/* Content Styles */
.popup-content {
  padding: 16px;
}

.geographic-section,
.population-section, 
.crime-section, 
.no-crime-section,
.no-filters-section,
.info-section {
  margin-bottom: 16px;
}

.geographic-section:last-child,
.population-section:last-child,
.crime-section:last-child,
.no-crime-section:last-child,
.no-filters-section:last-child,
.info-section:last-child {
  margin-bottom: 0;
}

.geographic-section h4,
.population-section h4, 
.crime-section h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #495057;
}

/* Geographic Information Styles */
.geographic-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.geographic-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #f1f3f4;
}

.geographic-item:last-child {
  border-bottom: none;
}

.geographic-item .label {
  font-size: 13px;
  color: #6c757d;
  font-weight: 500;
  min-width: 100px;
}

.geographic-item .value {
  font-size: 13px;
  font-weight: 500;
  color: #212529;
  text-align: right;
}

.code-value {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  background: #f8f9fa;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 12px;
}

/* Population Styles */
.population-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.population-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #f1f3f4;
}

.population-item:last-child {
  border-bottom: none;
}

.population-item .label {
  font-size: 13px;
  color: #6c757d;
  font-weight: 500;
  min-width: 100px;
}

.population-item .value {
  font-size: 13px;
  font-weight: 500;
  color: #212529;
  text-align: right;
}

.population-value {
  color: #059669;
  font-weight: 600;
}

.population-estimate {
  color: #6b7280;
  font-style: italic;
}

.estimate-note {
  font-size: 10px;
  color: #9ca3af;
  font-weight: normal;
  margin-left: 4px;
}

/* Crime Table Styles */
.crime-table-container {
  overflow-x: auto;
  margin-bottom: 12px;
}

.crime-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: white;
}

.crime-table th {
  background: #f8f9fa;
  padding: 8px 6px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  font-size: 12px;
}

.crime-table td {
  padding: 8px 6px;
  border-bottom: 1px solid #dee2e6;
  vertical-align: middle;
}

.crime-table tr.highlight {
  background-color: #fff5f5;
}

.crime-type .crime-label {
  font-weight: 500;
  color: #374151;
}

.crime-value .value-number {
  font-weight: 500;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

/* Risk Level Styles - 7 levels */
.crime-risk .risk-very_low,
.crime-risk .risk-very-low { 
  color: #059669;
  font-weight: 600;
}

.crime-risk .risk-low { 
  color: #22c55e;
  font-weight: 500;
}

.crime-risk .risk-low_moderate,
.crime-risk .risk-low-moderate { 
  color: #84cc16;
  font-weight: 500;
}

.crime-risk .risk-moderate { 
  color: #ca8a04;
  font-weight: 500;
}

.crime-risk .risk-moderate_high,
.crime-risk .risk-moderate-high { 
  color: #f97316;
  font-weight: 500;
}

.crime-risk .risk-high { 
  color: #ef4444;
  font-weight: 600;
}

.crime-risk .risk-very_high,
.crime-risk .risk-very-high { 
  color: #dc2626;
  font-weight: 700;
}

.crime-risk .risk-unknown {
  color: #6b7280;
  font-style: italic;
}

/* Legacy support for old 'elevated' and 'medium' classes */
.crime-risk .risk-elevated { 
  color: #f97316;
  font-weight: 500;
}

.crime-risk .risk-medium { 
  color: #ca8a04;
  font-weight: 500;
}

/* Intensity Summary Styles */
.intensity-summary {
  background: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  margin-top: 12px;
}

.intensity-bar-container {
  margin-bottom: 8px;
}

.intensity-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}

.intensity-bar {
  position: relative;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 4px;
}

.intensity-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease-in-out;
}

/* 7-level intensity bar colors matching map colors */
.intensity-fill.intensity-very-low {
  background: #059669; /* Dark green */
}

.intensity-fill.intensity-low {
  background: #22c55e; /* Green */
}

.intensity-fill.intensity-low-moderate {
  background: #84cc16; /* Yellow-green */
}

.intensity-fill.intensity-moderate {
  background: #eab308; /* Yellow */
}

.intensity-fill.intensity-moderate-high {
  background: #f97316; /* Orange */
}

.intensity-fill.intensity-high {
  background: #ef4444; /* Red */
}

.intensity-fill.intensity-very-high {
  background: #dc2626; /* Dark red */
}

.intensity-text {
  font-size: 12px;
  font-weight: 500;
  color: #374151;
}

.combined-value {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.combined-value .label {
  color: #6c757d;
}

.combined-value .value {
  font-weight: 600;
  color: #212529;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

/* No Filters Selected Styles */
.no-filters-section {
  text-align: center;
  padding: 20px 0;
}

.no-filters-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: #6b7280;
}

.info-icon {
  width: 28px;
  height: 28px;
  color: #3b82f6;
}

.no-filters-message p {
  margin: 0;
  font-size: 14px;
  text-align: center;
  color: #374151;
  font-weight: 500;
}

.filter-hint {
  background: #eff6ff;
  padding: 8px 12px;
  border-radius: 6px;
  border-left: 3px solid #3b82f6;
}

.filter-hint small {
  color: #1e40af;
  font-size: 12px;
}

/* No Crime Data Styles */
.no-crime-section {
  text-align: center;
  padding: 20px 0;
}

.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #6b7280;
}

.no-data-icon {
  width: 24px;
  height: 24px;
  color: #9ca3af;
}

.no-data-message p {
  margin: 0;
  font-size: 14px;
  text-align: center;
}

/* Debug Section Styles */
.debug-section {
  background: #fef3c7;
  border: 2px solid #f59e0b;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  max-height: 600px;
  overflow-y: auto;
}

.debug-section h4 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 700;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #fbbf24;
  padding-bottom: 8px;
}

.debug-subsection {
  background: white;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 10px;
  border-left: 3px solid #f59e0b;
}

.debug-subsection:last-of-type {
  margin-bottom: 0;
}

.debug-subsection h5 {
  margin: 0 0 8px 0;
  font-size: 12px;
  font-weight: 600;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.debug-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.debug-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 11px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  padding: 4px 6px;
  border-radius: 3px;
  background: #fefce8;
}

.debug-item.highlight {
  background: #fef08a;
  border: 1px solid #facc15;
  font-weight: 600;
}

.debug-item .label {
  color: #92400e;
  font-weight: 500;
  min-width: 140px;
  flex-shrink: 0;
}

.debug-item .value {
  color: #451a03;
  font-weight: 600;
  text-align: right;
  word-break: break-word;
}

.debug-item .value.formula {
  font-size: 10px;
  color: #78350f;
  font-style: italic;
}

.debug-item .value.highlight-value {
  background: #fef3c7;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 700;
  color: #92400e;
}

.debug-item.calculation-step {
  background: #fef9c3;
  border-left: 3px solid #facc15;
  padding-left: 10px;
}

.debug-item.interpretation {
  background: #dbeafe;
  border: 1px solid #60a5fa;
  padding: 8px;
  margin-top: 4px;
}

.debug-item.interpretation .label {
  color: #1e40af;
}

.debug-item.interpretation .value {
  color: #1e3a8a;
  font-size: 11px;
  line-height: 1.4;
}

.debug-item .value.color-swatch {
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #d97706;
  min-width: 80px;
  text-align: center;
}

.debug-summary {
  background: #fffbeb;
  border: 2px solid #fbbf24;
  border-radius: 6px;
  padding: 12px;
  margin-top: 12px;
  font-size: 12px;
  line-height: 1.6;
  color: #78350f;
}

.debug-summary strong {
  color: #92400e;
  font-weight: 700;
}

/* Risk Comparison Section Styles */
.risk-comparison-section {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 16px;
}

.risk-info-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.risk-info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #e0f2fe;
}

.risk-info-item:last-child {
  border-bottom: none;
}

.risk-info-item.comparison-ref {
  background: #e0f2fe;
  padding: 8px;
  border-radius: 4px;
  margin-top: 4px;
}

.risk-info-label {
  font-size: 13px;
  color: #0c4a6e;
  font-weight: 500;
}

.risk-info-value {
  font-size: 13px;
  font-weight: 600;
  color: #0369a1;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

.risk-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.risk-badge-very_low,
.risk-badge-very-low {
  background: #d1fae5;
  color: #065f46;
}

.risk-badge-low {
  background: #d1fae5;
  color: #047857;
}

.risk-badge-low_moderate,
.risk-badge-low-moderate {
  background: #ecfccb;
  color: #4d7c0f;
}

.risk-badge-moderate {
  background: #fef3c7;
  color: #92400e;
}

.risk-badge-moderate_high,
.risk-badge-moderate-high {
  background: #fed7aa;
  color: #9a3412;
}

.risk-badge-high {
  background: #fecaca;
  color: #991b1b;
}

.risk-badge-very_high,
.risk-badge-very-high {
  background: #fecaca;
  color: #7f1d1d;
}

/* Legacy support */
.risk-badge-elevated {
  background: #fed7aa;
  color: #9a3412;
}

/* Section Header with Info Icon */
.section-header-with-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.section-header-with-info h4 {
  margin: 0;
}

/* Label with Tooltip */
.label-with-tooltip {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Info Tooltip Styles */
.info-tooltip-container {
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;
}

.info-icon-small {
  width: 16px;
  height: 16px;
  color: #3b82f6;
  flex-shrink: 0;
}

.info-icon-tiny {
  width: 14px;
  height: 14px;
  color: #3b82f6;
  flex-shrink: 0;
}

.info-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  padding: 10px 12px;
  background: #1e293b;
  color: white;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.4;
  width: 240px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  pointer-events: none;
}

.info-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 6px solid transparent;
  border-top-color: #1e293b;
}

.info-tooltip-container:hover .info-tooltip {
  opacity: 1;
  visibility: visible;
}

.info-tooltip strong {
  display: block;
  margin-bottom: 4px;
  color: #60a5fa;
  font-size: 13px;
}

.info-tooltip p {
  margin: 0;
  color: #e2e8f0;
}

.info-tooltip p + p {
  margin-top: 6px;
}

.tooltip-example {
  font-size: 11px;
  color: #cbd5e1;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #334155;
}

/* Info Section Styles */
.info-section {
  border-top: 1px solid #e5e7eb;
  padding-top: 12px;
}

.data-note {
  margin: 0;
  font-size: 12px;
  color: #6c757d;
  font-style: italic;
  line-height: 1.4;
}

/* Accessibility Enhancements */
.crime-table:focus-within {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.crime-table th:focus,
.crime-table td:focus {
  outline: 2px solid #3b82f6;
  outline-offset: -2px;
  background-color: #eff6ff;
}

/* Mobile Responsive Styles */
@media (max-width: 480px) {
  .crime-popup {
    max-width: 100%;
    min-width: 280px;
  }
  
  .popup-header {
    padding: 12px;
  }
  
  .popup-content {
    padding: 12px;
  }
  
  .unit-name {
    font-size: 15px;
  }
  
  .crime-table {
    font-size: 12px;
  }
  
  .crime-table th,
  .crime-table td {
    padding: 6px 4px;
  }
  
  .unit-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }
}

/* High Contrast Mode Support */
@media (prefers-contrast: high) {
  .crime-popup {
    border: 2px solid #000;
  }
  
  .popup-header {
    border-bottom: 2px solid #000;
  }
  
  .crime-table th {
    border-bottom: 2px solid #000;
  }
  
  .crime-table td {
    border-bottom: 1px solid #000;
  }
}

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
  .intensity-fill {
    transition: none;
  }
}

/* Print Styles */
@media print {
  .crime-popup {
    box-shadow: none;
    border: 1px solid #000;
    max-width: none;
  }
  
  .popup-header {
    background: white;
    border-bottom: 1px solid #000;
  }
  
  .intensity-summary {
    background: white;
    border: 1px solid #ccc;
  }
}
</style>